/**
 * Edit profile — everything a member can change about themselves, in one
 * place. One sheet, one save button, no scattered controls. SPEC.md §7.3.
 *
 * The emergency number IS editable here, even though the profile page
 * deliberately does not display it. A member may update their own; they
 * simply never see anyone else's.
 *
 * The email is required and unique across live members: it is a login
 * identity and the only password-recovery channel.
 *
 * The emergency contact is OPTIONAL for everyone and takes any number —
 * mobile, landline or international (owner's direction, 2 Oct 2026; it used
 * to be required for runners and optional only for committee accounts). A
 * number that IS given must still be dialable and someone else's.
 */

import { useState } from 'react';
import {
  ageFrom,
  bibNo,
  emergencyPhoneError,
  normalizeEmergencyPhone,
  normalizeEmail,
} from '../../data';
import { useClub } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { PhotoAvatar } from '../PhotoBox';
import { AnyPhoneInput, Button, Field, FormError, Hint, Row2, SheetActions, SheetHead, TEXTAREA_CLS } from '../ui';
import { useGrounds } from '../../context/ClubContext';

export function ProfileSheet({ member }) {
  const grounds = useGrounds();
  const { updateMember } = useClub();
  const { closeSheet, toast } = useUI();

  const [form, setForm] = useState({
    phone: member.phone ?? '',
    email: member.email ?? '',
    dob: member.dob ?? '',
    gender: member.gender ?? '',
    ground: member.ground,
    tshirt: member.tshirt ?? '',
    emName: member.emName ?? '',
    emPhone: member.emPhone ?? '',
    emRel: member.emRel ?? '',
    blood: member.blood ?? '',
    medical: member.medical ?? '',
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const age = ageFrom(form.dob || null);
  const isMinor = age !== null && age < 18;

  async function save() {
    if (!form.phone.trim()) return setError('Mobile number is required.');
    if (!normalizeEmail(form.email)) {
      return setError(
        'A valid email address is required — you sign in with it, and it is where a password reset link goes.',
      );
    }
    // Optional — but a number that IS given must be dialable and must reach
    // someone OTHER than the member. See data/emergency.js (QA finding F-5).
    const emError = emergencyPhoneError(form.emPhone, form.phone);
    if (emError) return setError(emError);

    setSaving(true);
    try {
      await updateMember(member.id, {
        phone: form.phone.trim(),
        email: normalizeEmail(form.email),
        dob: form.dob || null,
        gender: form.gender || null,
        ground: form.ground,
        tshirt: form.tshirt.trim() || null,
        // Always sent: clearing the fields is a real choice now that the
        // contact is optional, and empty means "clear it".
        emName: form.emName.trim() || null,
        emPhone: normalizeEmergencyPhone(form.emPhone),
        emRel: form.emRel.trim() || null,
        blood: form.blood.trim() || null,
        medical: form.medical.trim() || null,
      });
      closeSheet();
      toast('Profile updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setSaving(false);
    }
  }

  return (
    <>
      <SheetHead
        title="Edit profile"
        sub={member.bib ? `Bib ${bibNo(member.bib)} · ${member.name}` : member.name}
      />

      <PhotoAvatar member={member} />

      <div className="mb-3 font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted">
        Personal details
      </div>

      <Row2>
        <Field label="Mobile number" required>
          <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
        </Field>
        <Field label="Date of birth">
          <input type="date" value={form.dob} onChange={(e) => set('dob', e.target.value)} />
        </Field>
      </Row2>

      <Row2>
        <Field label="Gender">
          <select value={form.gender} onChange={(e) => set('gender', e.target.value)}>
            <option value="">—</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="X">Other</option>
          </select>
        </Field>
        <Field label="Home ground">
          <select value={form.ground} onChange={(e) => set('ground', e.target.value)}>
            {Object.entries(grounds).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
      </Row2>

      <Field label="Email address" required>
        <input
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
          placeholder="you@email.com"
        />
      </Field>
      <Hint style={{ marginTop: -8, marginBottom: 14 }}>
        A sign-in identity, and the only place a password reset link can be sent. Changing it
        changes where that email goes.
      </Hint>

      <Field label="T-shirt size">
        <input
          value={form.tshirt}
          onChange={(e) => set('tshirt', e.target.value)}
          placeholder="S / M / L / XL"
        />
      </Field>

      <div className="mb-3 mt-5 font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted">
        Emergency &amp; medical
      </div>

      <Field label={`${isMinor ? 'Guardian' : 'Emergency contact'} name`}>
        <input
          value={form.emName}
          onChange={(e) => set('emName', e.target.value)}
          placeholder="e.g. R. Priya"
        />
      </Field>

      <Row2>
        <Field label="Their phone">
          <AnyPhoneInput
            name="profile-em-phone"
            value={form.emPhone}
            onChange={(v) => set('emPhone', v)}
            placeholder="Any number"
          />
        </Field>
        <Field label="Relationship">
          <input
            value={form.emRel}
            onChange={(e) => set('emRel', e.target.value)}
            placeholder="Wife / Father / Friend"
          />
        </Field>
      </Row2>

      <Field label="Blood group">
        <input value={form.blood} onChange={(e) => set('blood', e.target.value)} placeholder="O+" />
      </Field>

      <Field label="Medical notes (optional)">
        <textarea
          className={TEXTAREA_CLS}
          value={form.medical}
          onChange={(e) => set('medical', e.target.value)}
          placeholder="Allergies, conditions, medication"
        />
      </Field>

      <Hint>
        Seen only by the club committee, and used only in an emergency.
        {' Optional — but fill it in if you run with the club.'}
      </Hint>
      <FormError>{error}</FormError>

      <SheetActions>
        <Button tone="g" onClick={closeSheet}>
          Cancel
        </Button>
        <Button tone="p" onClick={save} disabled={saving}>
          Save changes
        </Button>
      </SheetActions>
    </>
  );
}
